import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker, Field } from '~/components/Form';

type FormWithExpirationDate = {
  expirationDate: Date | null;
};

type Props<T extends FormWithExpirationDate> = {
  form: T extends FormWithExpirationDate ? UseFormReturn<T> : never;
};

export const FormExpirationDate = <T extends FormWithExpirationDate>({ form: { control } }: Props<T>) => {
  const { t } = useTranslation(['quotations', 'validation']);

  // Recomputing this each render would hand the date picker a new timestamp every time, which resets its display value.
  const minDate = useMemo(() => dayjs().add(1, 'day').toDate(), []);

  return (
    <Controller
      control={control}
      name="expirationDate"
      render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label>{t('quotations:fields.expirationDate.label')}</Field.Label>

          <DatePicker minDate={minDate} onBlur={onBlur} onChange={onChange} ref={ref} value={value} />

          <Field.Error match={!!error}>{error?.message}</Field.Error>
        </Field.Root>
      )}
      rules={{
        required: { value: true, message: t('validation:required') },
        validate: (value) => dayjs(value).isAfter(dayjs().endOf('day')) || t('validation:dateTomorrow'),
      }}
    />
  );
};
